// Single source of truth for the cached user object stored in localStorage.
// Avoids the ~10 duplicate try/catch readers scattered around the codebase.

import { STORAGE_KEYS } from '../constants/storageKeys';

export interface CachedUserShape {
    uid?: string;
    name?: string;
    mobile?: string;
    status?: string;
    userType?: string;
    societyDetails?: {
        name?: string;
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        country?: string;
        zipCode?: string;
        landmark?: string;
        fundMode?: FundMode;
    };
    address?: { societyName?: string; wing?: string; homeNo?: string };
    // SOCIETY_ADMIN only (from GET /v1/user): effective admin scope; wing fields for WING.
    adminScope?: AdminScope | null;
    adminWingId?: number | null;
    adminWingName?: string | null;
    [key: string]: any;
}

export type AdminScope = 'SOCIETY' | 'WING';

export interface AdminScopeInfo {
    /** null when the caller isn't a SOCIETY_ADMIN. */
    scope: AdminScope | null;
    /** WING scope only. */
    wingId: number | null;
    wingName: string | null;
}

export function getCachedUser(): CachedUserShape | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.USER);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
        return parsed as CachedUserShape;
    } catch {
        return null;
    }
}

export function getCachedUserSocietyName(): string {
    const u = getCachedUser();
    return (u?.societyDetails?.name || u?.address?.societyName || '').toString();
}

export function getUserType(): string {
    return getCachedUser()?.userType ?? '';
}

export function getUid(): string {
    return getCachedUser()?.uid ?? '';
}

export const isAdmin      = (): boolean => getUserType() === 'SOCIETY_ADMIN';
export const isGuard      = (): boolean => getUserType() === 'GUARD';
export const isAgent      = (): boolean => getUserType() === 'AGENT';
export const isDailyHelp   = (): boolean => getUserType() === 'DAILY_HELP';
export const isResident    = (): boolean => getUserType() === 'RESIDENTIAL';

/**
 * The cached admin's scope, as the server reported it on the last profile fetch. A cache
 * written before the server sent `adminScope` falls back to the server's own legacy rule
 * (no home = SOCIETY, a home = WING of that home's wing) until the next profile refresh.
 */
export function getAdminScopeInfo(): AdminScopeInfo {
    const u = getCachedUser();
    if (u?.userType !== 'SOCIETY_ADMIN') return { scope: null, wingId: null, wingName: null };
    if (u.adminScope === 'SOCIETY' || u.adminScope === 'WING') {
        return {
            scope: u.adminScope,
            wingId: u.adminScope === 'WING' ? (u.adminWingId ?? null) : null,
            wingName: u.adminScope === 'WING' ? (u.adminWingName ?? u.address?.wing ?? null) : null,
        };
    }
    const legacyWing = (u.address?.wing ?? '').toString();
    return legacyWing
        ? { scope: 'WING', wingId: null, wingName: legacyWing }
        : { scope: 'SOCIETY', wingId: null, wingName: null };
}

export const isSocietyScopeAdmin = (): boolean => getAdminScopeInfo().scope === 'SOCIETY';
export const isWingScopeAdmin    = (): boolean => getAdminScopeInfo().scope === 'WING';

export type FundMode = 'SOCIETY_WIDE' | 'WING_WISE';

/**
 * The society's fund mode, as the server reported it on the last profile fetch. Absent on
 * a cache written before this field existed — defaults to WING_WISE, matching the backend's
 * own default, until the next profile refresh lands.
 */
export function getFundMode(): FundMode {
    return getCachedUser()?.societyDetails?.fundMode ?? 'WING_WISE';
}

/**
 * Filters a society's wings to the ones this admin manages: every wing for a
 * society-scope admin (and for non-admins), just their own wing for a wing admin.
 */
export function wingsManagedBy<W extends { wingId: number; name: string }>(info: AdminScopeInfo, wings: W[]): W[] {
    if (info.scope !== 'WING') return wings;
    return wings.filter(w => info.wingId != null ? w.wingId === info.wingId : w.name === info.wingName);
}

/**
 * Whether this admin may manage (edit/delete/close/change status of) a row scoped to
 * `rowWingId` (null/undefined = society-wide) — mirrors the backend's assertCanManage.
 * Society-scope admin: every row. Wing admin: only rows of their own wing (society-wide
 * rows and their home wing's rows are view-only). Non-admin: never.
 */
export function canManageRow(info: AdminScopeInfo, rowWingId: number | null | undefined): boolean {
    if (info.scope === 'SOCIETY') return true;
    if (info.scope === 'WING') return rowWingId != null && info.wingId != null && rowWingId === info.wingId;
    return false;
}

// ── Society config cache (3-hour TTL) ─────────────────────────────────────────

const SOCIETY_CONFIG_KEY = 'society_config_cache';
const SOCIETY_CONFIG_TTL = 3 * 60 * 60 * 1000; // 3 hours

export interface CachedSocietyConfig {
    autoApproveGuardEntry?: boolean;
    guardCanAddVehicle?: boolean;
    [key: string]: any;
}

export function getCachedSocietyConfig(): CachedSocietyConfig | null {
    try {
        const raw = localStorage.getItem(SOCIETY_CONFIG_KEY);
        if (!raw) return null;
        const { config, timestamp } = JSON.parse(raw);
        if (Date.now() - timestamp > SOCIETY_CONFIG_TTL) {
            localStorage.removeItem(SOCIETY_CONFIG_KEY);
            return null;
        }
        return config as CachedSocietyConfig;
    } catch {
        return null;
    }
}

export function cacheSocietyConfig(config: CachedSocietyConfig): void {
    try {
        localStorage.setItem(SOCIETY_CONFIG_KEY, JSON.stringify({ config, timestamp: Date.now() }));
    } catch { /* storage full — skip */ }
}
